// Presentation tweaks for the standard dashboard (ID 0) driven by end-user feedback:
//  - Show every grid column (with headers) on mobile. DevExpress' mobile layout enables
//    columnHidingEnabled by default, which hides columns on narrow screens.
//  - Add an explicit "Se mer" (data items) / "Endre" (filter items) caption button so users
//    discover that an item can be maximized, instead of relying on tapping the frame.
//  - Keep those buttons in staticItems: actionItems are hover-only on desktop layout.
//  - Strip noisy English measure names from the Salgsoversikt card caption.

export const STANDARD_DASHBOARD_ID = '0';

const CHART_ITEM = 'chartDashboardItem1';
const CARD_ITEM = 'cardDashboardItem1';
const SALES_PER_DAY_GRID = 'gridDashboardItem1';
const ARTICLES_GRID = 'gridDashboardItem2';
const MONTH_FILTER = 'comboBoxDashboardItem1';
const YEAR_FILTER = 'comboBoxDashboardItem2';
const SUPPLIER_FILTER = 'listBoxDashboardItem1';

const STANDARD_GRID_ITEMS = new Set([SALES_PER_DAY_GRID, ARTICLES_GRID]);
const SEE_MORE_ITEMS = new Set([CHART_ITEM, CARD_ITEM, SALES_PER_DAY_GRID, ARTICLES_GRID]);
const EDIT_FILTER_ITEMS = new Set([MONTH_FILTER, YEAR_FILTER, SUPPLIER_FILTER]);

const SEE_MORE_BUTTON_NAME = 'std-see-more-action';
const ITEM_CAPTION_NAME = 'item-caption';
const MAXIMIZE_ITEM_NAME = 'maximize-item';

function getDashboardItemName(args) {
    const item = args.getDashboardItem?.() ?? args.dashboardItem;
    return item?.componentName?.() ?? item?.name?.() ?? item?.name;
}

function ensureArray(options, key) {
    if (!Array.isArray(options[key])) {
        options[key] = [];
    }
    return options[key];
}

function cleanCardCaption(staticItems) {
    for (const item of staticItems) {
        if (!item || (item.name !== ITEM_CAPTION_NAME && item.type !== 'text')) {
            continue;
        }
        if (typeof item.text === 'string' && item.text.startsWith('Salgsoversikt')) {
            item.text = 'Salgsoversikt';
        }
    }
}

/**
 * Keeps all grid columns and their headers visible on mobile (feedback: "Vise innholdet i alle
 * kolonnene" / "Vise kolonnenavn"). Only affects the two standard-dashboard grids.
 */
export function onStandardGridWidgetOptionsPrepared(args) {
    if (!STANDARD_GRID_ITEMS.has(getDashboardItemName(args))) {
        return;
    }
    const options = args.options;
    if (!options) {
        return;
    }
    options.columnHidingEnabled = false;
    options.columnAutoWidth = true;
}

/**
 * Builds an onItemCaptionToolbarUpdated handler that adds a "Se mer" / "Endre" button to
 * standard-dashboard items. The button maximizes the item via the public DashboardControl API.
 *
 * @param {() => object} getDashboardControl Lazily returns the DashboardControl instance.
 */
export function createStandardCaptionToolbarHandler(getDashboardControl) {
    return function onItemCaptionToolbarUpdated(e) {
        try {
            const control = getDashboardControl();
            if (!control || typeof control.maximizeDashboardItem !== 'function') {
                return;
            }
            if (typeof control.getDashboardId === 'function'
                && String(control.getDashboardId()) !== STANDARD_DASHBOARD_ID) {
                return;
            }
            if (!e?.options) {
                return;
            }

            // When the item is already maximized the caption shows the built-in restore action.
            if (control.maximizedDashboardItemName === e.itemName) {
                return;
            }

            const staticItems = ensureArray(e.options, 'staticItems');
            const actionItems = ensureArray(e.options, 'actionItems');

            if (e.itemName === CARD_ITEM) {
                cleanCardCaption(staticItems);
            }

            let label = null;
            if (SEE_MORE_ITEMS.has(e.itemName)) {
                label = 'Se mer';
            } else if (EDIT_FILTER_ITEMS.has(e.itemName)) {
                label = 'Endre';
            }
            if (!label) {
                return;
            }

            // Prefer staticItems so the label stays visible without hover (desktop layout).
            if (staticItems.some((item) => item?.name === SEE_MORE_BUTTON_NAME)) {
                return;
            }

            // Avoid duplicating the built-in maximize icon when we show an explicit label.
            const maximizeIndex = actionItems.findIndex((item) => item?.name === MAXIMIZE_ITEM_NAME);
            if (maximizeIndex >= 0) {
                actionItems.splice(maximizeIndex, 1);
            }

            staticItems.push({
                type: 'button',
                name: SEE_MORE_BUTTON_NAME,
                text: label,
                hint: label,
                click: () => {
                    try {
                        control.maximizeDashboardItem(e.itemName);
                    } catch {
                        // Ignore: maximizing is best-effort.
                    }
                }
            });
        } catch {
            // Never break caption rendering.
        }
    };
}
